import { FunctionDeclaration, Type } from "@google/genai";

export const adhdFunctionDeclarations: FunctionDeclaration[] = [
  {
    name: "create_todo",
    description:
      "Create a new todo item with automatic estimation and rebalancing. TRIGGER WORDS: 'add', 'create', 'new task', 'I need to', 'I have to', 'I should', 'remind me', 'don't forget', 'put on list', 'schedule'. ALSO TRIGGER for imperative statements like 'Buy milk', 'Call doctor', 'Finish report'. Automatically estimates all parameters based on existing workload and rebalances all tasks for optimal workflow.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        title: {
          type: Type.STRING,
          description: "Todo item title - be specific and actionable",
        },
        description: {
          type: Type.STRING,
          description: "Detailed description with context and steps if needed",
        },
        priority: {
          type: Type.STRING,
          enum: ["low", "medium", "high", "urgent"],
          description: "Priority level - urgent for deadline-driven, high for important work",
        },
        due_date: {
          type: Type.STRING,
          description:
            "Due date in ISO 8601 datetime format (e.g., '2025-07-04T10:00:00Z'). Convert natural language dates to this format.",
        },
        project_id: {
          type: Type.STRING,
          description: "Associated project ID to group related tasks",
        },
        tags: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Tags for categorization like 'work', 'personal', 'quick'",
        },
        estimated_minutes: {
          type: Type.NUMBER,
          description: "Estimated time to complete in minutes for time blocking",
        },
        notes: {
          type: Type.STRING,
          description: "Additional notes, context, or implementation details for the todo",
        },
        use_smart_placement: {
          type: Type.BOOLEAN,
          description:
            "Enable AI-powered intelligent project assignment for better organization (default: true)",
        },
        // ADHD-specific fields for executive function support
        adhd_energy_level: {
          type: Type.STRING,
          enum: ["low", "medium", "high"],
          description:
            "Energy level required: 'low' for minimal cognitive effort, 'medium' for moderate focus, 'high' for deep focus/complex work",
        },
        adhd_context_cost: {
          type: Type.NUMBER,
          description:
            "Context switching complexity (1-10): 1=minimal setup, 5=average, 10=deep context required. Consider task setup time and mental switching costs.",
        },
        adhd_hyperfocus_compatible: {
          type: Type.BOOLEAN,
          description:
            "True if task benefits from sustained deep work sessions (2+ hours of uninterrupted focus). Good for creative work, coding, writing, research.",
        },
      },
      required: ["title"],
    },
  },

  {
    name: "create_project",
    description:
      "Create a new project to organize related todos for better ADHD focus and context switching",
    parameters: {
      type: Type.OBJECT,
      properties: {
        name: {
          type: Type.STRING,
          description: "Project name - clear and descriptive",
        },
        description: {
          type: Type.STRING,
          description: "Project description with goals and context",
        },
        color: {
          type: Type.STRING,
          description:
            "Color theme in hex format (e.g., '#ff0000') or common color names (e.g., 'red', 'blue', 'green')",
        },
        deadline: {
          type: Type.STRING,
          description:
            "Project deadline in ISO 8601 datetime format (e.g., '2025-07-04T10:00:00Z'). Convert natural language dates to this format.",
        },
      },
      required: ["name"],
    },
  },

  {
    name: "update_project",
    description: "Update existing project details like name, description, color, or deadline",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_id: {
          type: Type.STRING,
          description: "Unique identifier of the project to update",
        },
        name: {
          type: Type.STRING,
          description: "Updated project name",
        },
        description: {
          type: Type.STRING,
          description: "Updated project description",
        },
        color: {
          type: Type.STRING,
          description: "Updated color theme for visual organization",
        },
        deadline: {
          type: Type.STRING,
          description:
            "Updated project deadline in ISO 8601 datetime format (e.g., '2025-07-04T10:00:00Z')",
        },
      },
      required: ["project_id"],
    },
  },

  {
    name: "delete_project",
    description:
      "Delete a project and unassign all its todos (todos remain but lose project association)",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_id: {
          type: Type.STRING,
          description: "Unique identifier of the project to delete",
        },
      },
      required: ["project_id"],
    },
  },

  {
    name: "get_todos",
    description:
      "Retrieve todos for analysis and display. TRIGGER WORDS: 'show', 'list', 'what', 'my tasks', 'what's due', 'what's urgent', 'check todos'. ALWAYS call this before creating new tasks to analyze existing workload. Returns focused lists based on filters - 'today' for daily focus, 'high_priority' for urgent work.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        filter_type: {
          type: Type.STRING,
          enum: ["all", "today", "overdue", "high_priority", "project"],
          description: "Filter type: 'today' for daily focus, 'high_priority' for urgent tasks",
        },
        project_id: {
          type: Type.STRING,
          description: "Get todos for specific project to maintain context",
        },
        available_time_minutes: {
          type: Type.NUMBER,
          description: "Minutes available to filter for tasks that fit the time block",
        },
      },
    },
  },

  {
    name: "get_projects",
    description:
      "Retrieve all projects to see available project contexts for organizing todos and maintaining ADHD focus",
    parameters: {
      type: Type.OBJECT,
      properties: {},
    },
  },

  {
    name: "update_todo",
    description:
      "Update existing todo item status, priority, project assignment, or add progress notes. IMPORTANT: Use the todo's 'id' field, NOT the title. Can also move tasks between projects.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        todo_id: {
          type: Type.STRING,
          description:
            "Unique identifier (ID field) of the todo item to update. This must be the 'id' value from the todo object, NOT the title or description.",
        },
        status: {
          type: Type.STRING,
          enum: ["pending", "in_progress", "completed", "blocked"],
          description: "New status - use 'blocked' when stuck to prevent rumination",
        },
        priority: {
          type: Type.STRING,
          enum: ["low", "medium", "high", "urgent"],
          description: "Updated priority level",
        },
        due_date: {
          type: Type.STRING,
          description:
            "Updated due date in ISO 8601 datetime format (e.g., '2025-07-04T10:00:00Z')",
        },
        project_id: {
          type: Type.STRING,
          description:
            "Move task to a different project by specifying the project ID. Use null or empty string to unassign from project.",
        },
        estimated_minutes: {
          type: Type.NUMBER,
          description: "Updated estimated time to complete in minutes",
        },
        notes: {
          type: Type.STRING,
          description: "Add progress notes, obstacles, or context for future reference",
        },
      },
      required: ["todo_id"],
    },
  },

  {
    name: "update_multiple_todos",
    description:
      "Update multiple todo items at once for dynamic task rebalancing. CRITICAL: Always use after creating new tasks to rebalance workload, adjust priorities, redistribute due dates, and optimize the entire task ecosystem. This is MANDATORY for automatic task management.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        updates: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              todo_id: {
                type: Type.STRING,
                description:
                  "Unique identifier (ID field) of the todo item to update. Use the 'id' value, NOT the title.",
              },
              status: {
                type: Type.STRING,
                enum: ["pending", "in_progress", "completed", "blocked"],
                description: "New status",
              },
              priority: {
                type: Type.STRING,
                enum: ["low", "medium", "high", "urgent"],
                description: "Updated priority level",
              },
              due_date: {
                type: Type.STRING,
                description: "Updated due date in ISO 8601 datetime format",
              },
              estimated_minutes: {
                type: Type.NUMBER,
                description: "Updated estimated time to complete in minutes",
              },
              notes: {
                type: Type.STRING,
                description: "Reason for the update (e.g., 'Rebalanced due to new urgent task')",
              },
            },
            required: ["todo_id"],
          },
          description: "Array of todo updates to apply",
        },
        rebalance_reason: {
          type: Type.STRING,
          description: "Brief explanation of why these tasks were rebalanced",
        },
      },
      required: ["updates"],
    },
  },

  {
    name: "delete_todo",
    description:
      "Delete a todo item that's no longer relevant or needed. IMPORTANT: Use the todo's 'id' field, NOT the title or any other field.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        todo_id: {
          type: Type.STRING,
          description:
            "Unique identifier (ID field) of the todo item to delete. This must be the 'id' value from the todo object, NOT the title or description. Example: '123abc' or 'todo_456', NOT 'Buy groceries' or any human-readable text.",
        },
      },
      required: ["todo_id"],
    },
  },

  {
    name: "get_focus_suggestions",
    description:
      "Get personalized task suggestions. TRIGGER WORDS: 'what should I', 'what to work on', 'I'm stuck', 'help me focus', 'can't decide', 'feeling overwhelmed', 'where to start', 'next task', 'help me prioritize'. Consider energy level, available time, and current context to prevent overwhelm.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        available_time_minutes: {
          type: Type.NUMBER,
          description: "Minutes available for productive work",
        },
        current_energy_level: {
          type: Type.STRING,
          enum: ["low", "medium", "high"],
          description: "Current energy level for matching appropriate task complexity",
        },
        current_context: {
          type: Type.STRING,
          description: "Where user is or what they're doing for context-appropriate suggestions",
        },
        prefer_hyperfocus: {
          type: Type.BOOLEAN,
          description:
            "Whether to prioritize tasks suitable for hyperfocus sessions (longer, complex tasks)",
        },
      },
    },
  },

  {
    name: "start_body_doubling",
    description:
      "Start a focus session with AI accountability. TRIGGER WORDS: 'help me focus', 'can't start', 'I need accountability', 'body double', 'work with me', 'keep me company', 'procrastinating'. Provides companionship and check-ins without judgment for ADHD executive dysfunction.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_id: {
          type: Type.STRING,
          description: "ID of the task to work on during the session",
        },
        duration_minutes: {
          type: Type.NUMBER,
          description: "Length of focused work session",
        },
        check_in_frequency: {
          type: Type.NUMBER,
          description: "Minutes between gentle check-ins and encouragement",
        },
      },
    },
  },

  {
    name: "set_hyperfocus_break",
    description:
      "Set break reminders during hyperfocus. TRIGGER WORDS: 'been working for hours', 'remind me to take breaks', 'need water', 'forgot to eat', 'hyperfocusing', 'in the zone', 'lost track of time'. Maintains healthy patterns without breaking flow state.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        break_interval: {
          type: Type.NUMBER,
          description: "Minutes between break reminders during hyperfocus",
        },
        break_type: {
          type: Type.STRING,
          enum: ["water", "movement", "eyes", "full"],
          description: "Type of break: water, movement, eye rest, or full break",
        },
      },
    },
  },

  {
    name: "suggest_project_assignments",
    description:
      "Get AI-powered suggestions for organizing unassigned tasks into appropriate projects",
    parameters: {
      type: Type.OBJECT,
      properties: {
        limit: {
          type: Type.NUMBER,
          description: "Maximum number of unassigned tasks to analyze (default: 10)",
        },
      },
    },
  },

  {
    name: "apply_project_assignments",
    description: "Apply AI-suggested project assignments to organize tasks better",
    parameters: {
      type: Type.OBJECT,
      properties: {
        assignments: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              todo_id: { type: Type.STRING, description: "ID of the task to assign" },
              project_id: { type: Type.STRING, description: "ID of the project to assign it to" },
              confidence: { type: Type.NUMBER, description: "Confidence score (0-100)" },
            },
            required: ["todo_id", "project_id", "confidence"],
          },
          description: "Array of task-to-project assignments to apply",
        },
      },
      required: ["assignments"],
    },
  },

  {
    name: "reprioritize_all_tasks",
    description:
      "Reorganize all tasks using Eisenhower Matrix. TRIGGER WORDS: 'organize', 'prioritize', 'everything feels urgent', 'scattered', 'help me organize', 'sort my tasks', 'what's important'. Balances urgent vs important work while preventing overwhelm.",
    parameters: {
      type: Type.OBJECT,
      properties: {},
    },
  },

  {
    name: "get_tasks_by_quadrant",
    description:
      "Retrieve tasks filtered by Eisenhower Matrix quadrant (1=Do First, 2=Schedule, 3=Delegate, 4=Delete) with ADHD-friendly sorting",
    parameters: {
      type: Type.OBJECT,
      properties: {
        quadrant: {
          type: Type.NUMBER,
          description:
            "Eisenhower quadrant: 1=Do First (urgent+important), 2=Schedule (important), 3=Delegate (urgent), 4=Delete (neither)",
        },
        include_adhd_context: {
          type: Type.BOOLEAN,
          description:
            "Include ADHD-specific context like energy level and context switching costs in sorting",
        },
      },
      required: ["quadrant"],
    },
  },

  {
    name: "get_prioritization_insights",
    description:
      "Analyze current task prioritization quality and get suggestions for improvement, including confidence scores and quadrant distribution",
    parameters: {
      type: Type.OBJECT,
      properties: {},
    },
  },

  {
    name: "analyze_task_priority",
    description:
      "Manually analyze and get detailed priority breakdown for a specific task using Eisenhower Matrix with confidence scores",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_id: {
          type: Type.STRING,
          description: "ID of the task to analyze for detailed priority breakdown",
        },
      },
      required: ["task_id"],
    },
  },

  {
    name: "set_energy_context",
    description:
      "Set current energy level and context to get better task recommendations (affects automatic prioritization)",
    parameters: {
      type: Type.OBJECT,
      properties: {
        energy_level: {
          type: Type.STRING,
          enum: ["low", "medium", "high"],
          description:
            "Current energy level (low=simple tasks, medium=moderate complexity, high=complex tasks)",
        },
        context_description: {
          type: Type.STRING,
          description:
            "Current context or environment (e.g., 'at office', 'working from home', 'on phone')",
        },
        available_time_minutes: {
          type: Type.NUMBER,
          description: "How much focused time is available right now",
        },
      },
      required: ["energy_level"],
    },
  },

  // Voice Navigation Functions for Collapsible Project Tree
  {
    name: "expand_project",
    description:
      "Expand a specific project to show its tasks in the hierarchy view. Use when user says 'expand project X' or 'show tasks for project X'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_name: {
          type: Type.STRING,
          description:
            "Name of the project to expand (will be matched against existing project names)",
        },
        project_id: {
          type: Type.STRING,
          description: "Specific project ID if known (preferred over name matching)",
        },
      },
    },
  },

  {
    name: "collapse_project",
    description:
      "Collapse a specific project to hide its tasks in the hierarchy view. Use when user says 'collapse project X' or 'hide tasks for project X'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_name: {
          type: Type.STRING,
          description:
            "Name of the project to collapse (will be matched against existing project names)",
        },
        project_id: {
          type: Type.STRING,
          description: "Specific project ID if known (preferred over name matching)",
        },
      },
    },
  },

  {
    name: "expand_all_projects",
    description:
      "Expand all projects to show all tasks in the hierarchy view. Use when user says 'expand all projects', 'show all tasks', or 'open everything'",
    parameters: {
      type: Type.OBJECT,
      properties: {},
    },
  },

  {
    name: "collapse_all_projects",
    description:
      "Reduce visual overwhelm by hiding task details. TRIGGER WORDS: 'overwhelmed', 'too much', 'stressed', 'can't focus', 'focus mode', 'minimize', 'simplify', 'clean view', 'hide details'. IMMEDIATE action for ADHD overwhelm management.",
    parameters: {
      type: Type.OBJECT,
      properties: {},
    },
  },

  {
    name: "focus_project",
    description:
      "Navigate to and focus on a specific project in the hierarchy. Use when user says 'go to project X', 'focus on project X', or 'select project X'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_name: {
          type: Type.STRING,
          description:
            "Name of the project to focus on (will be matched against existing project names)",
        },
        project_id: {
          type: Type.STRING,
          description: "Specific project ID if known (preferred over name matching)",
        },
      },
    },
  },

  {
    name: "focus_task",
    description:
      "Navigate to and focus on a specific task in the hierarchy. Use when user says 'go to task X', 'find task X', or 'select task X'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_title: {
          type: Type.STRING,
          description:
            "Title of the task to focus on (will be matched against existing task titles)",
        },
        task_id: {
          type: Type.STRING,
          description: "Specific task ID if known (preferred over title matching)",
        },
      },
    },
  },

  // Task-Project Movement Functions
  {
    name: "move_task_to_project",
    description:
      "Move an existing task to a different project or unassign it from projects. Perfect for when users say 'move task X to project Y', 'put this task in the shopping project', 'assign task to project', or 'remove task from project'. Automatically finds tasks and projects by name.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_identifier: {
          type: Type.STRING,
          description:
            "Task title, partial title, or task ID to identify which task to move. Will search by title if not a valid ID.",
        },
        project_identifier: {
          type: Type.STRING,
          description:
            "Project name, partial project name, or project ID. Use 'none', 'unassigned', or null to remove task from all projects.",
        },
      },
      required: ["task_identifier"],
    },
  },

  {
    name: "find_task_by_name",
    description:
      "Find tasks by partial or full title match. Use when user references a task by name and you need to identify the specific task ID.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_name: {
          type: Type.STRING,
          description: "Full or partial task name to search for",
        },
        exact_match: {
          type: Type.BOOLEAN,
          description:
            "Whether to require exact match or allow partial matching (default: false for partial matching)",
        },
      },
      required: ["task_name"],
    },
  },

  {
    name: "find_project_by_name",
    description:
      "Find projects by partial or full name match. Use when user references a project by name and you need to identify the specific project ID.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        project_name: {
          type: Type.STRING,
          description: "Full or partial project name to search for",
        },
        exact_match: {
          type: Type.BOOLEAN,
          description:
            "Whether to require exact match or allow partial matching (default: false for partial matching)",
        },
      },
      required: ["project_name"],
    },
  },

  {
    name: "batch_move_tasks_to_project",
    description:
      "Move multiple tasks to the same project at once. Use for bulk operations like 'move all shopping tasks to shopping project' or 'put these tasks in the work project'.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_identifiers: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Array of task titles, partial titles, or task IDs to move",
        },
        project_identifier: {
          type: Type.STRING,
          description:
            "Project name, partial project name, or project ID where tasks should be moved. Use 'none' or 'unassigned' to remove from all projects.",
        },
      },
      required: ["task_identifiers", "project_identifier"],
    },
  },

  {
    name: "smart_project_assignment",
    description:
      "Use AI to intelligently suggest and apply project assignments for tasks based on their content. Perfect for organizing existing unassigned tasks or when user says 'organize my tasks' or 'suggest project assignments'.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        task_identifiers: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description:
            "Optional array of specific task identifiers to analyze. If omitted, analyzes all unassigned tasks.",
        },
        auto_apply: {
          type: Type.BOOLEAN,
          description:
            "Whether to automatically apply high-confidence suggestions (>80% confidence) or just return suggestions for user review (default: false)",
        },
        create_new_projects: {
          type: Type.BOOLEAN,
          description:
            "Whether to suggest creating new projects for tasks that don't fit existing projects (default: true)",
        },
      },
    },
  },

  {
    name: "get_subscription_info",
    description:
      "Get current subscription plan details, usage limits, and pricing information. Use this when users hit limits, ask about upgrades, or need subscription assistance. I can act as a sales assistant with this information.",
    parameters: {
      type: Type.OBJECT,
      properties: {
        include_pricing: {
          type: Type.BOOLEAN,
          description:
            "Whether to include available upgrade plan pricing information (default: true)",
        },
        include_usage: {
          type: Type.BOOLEAN,
          description: "Whether to include current usage statistics and limits (default: true)",
        },
      },
    },
  },

  {
    name: "assign_todo_to_project",
    description:
      "Assign an existing todo to a project for better organization. TRIGGER WORDS: 'move to project', 'assign to', 'organize', 'put in project', 'belongs to', 'categorize'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        todo_id: {
          type: Type.STRING,
          description: "ID of the todo to assign to a project",
        },
        project_id: {
          type: Type.STRING,
          description: "ID of the project to assign the todo to",
        },
      },
      required: ["todo_id", "project_id"],
    },
  },

  {
    name: "organize_todos_into_projects",
    description:
      "Batch organize multiple todos into appropriate projects. TRIGGER WORDS: 'organize my tasks', 'sort my todos', 'categorize everything', 'clean up my list', 'group tasks', 'arrange by project'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        assignments: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              todo_id: {
                type: Type.STRING,
                description: "ID of the todo to assign",
              },
              project_id: {
                type: Type.STRING,
                description: "ID of the project to assign to",
              },
            },
          },
          description: "Array of todo-to-project assignments",
        },
        create_new_projects: {
          type: Type.BOOLEAN,
          description:
            "Whether to create new projects if needed for better organization (default: false)",
        },
      },
      required: ["assignments"],
    },
  },

  {
    name: "suggest_project_organization",
    description:
      "Get AI suggestions for organizing unassigned todos into projects. TRIGGER WORDS: 'how should I organize', 'suggest organization', 'help me sort', 'what projects do I need'",
    parameters: {
      type: Type.OBJECT,
      properties: {
        include_all_todos: {
          type: Type.BOOLEAN,
          description: "Whether to include all todos or just unassigned ones (default: false)",
        },
        suggest_new_projects: {
          type: Type.BOOLEAN,
          description: "Whether to suggest creating new projects (default: true)",
        },
      },
    },
  },
];
